<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-800 to-gray-900">
    <!-- Header -->
    <header class="w-full px-[12.5%] py-8 backdrop-blur-sm bg-gray-800/50 border-b border-gray-700">
      <div class="max-w-6xl mx-auto">
        <h1 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">Resources</h1>
        <p class="text-xl text-gray-400">Free tools, books, and recommendations for creators</p>
      </div>
    </header>

    <!-- Main Content -->
    <main class="w-full px-[12.5%] py-12">
      <div class="max-w-6xl mx-auto">
        <!-- Search & Filters -->
        <div class="mb-12 animate-fade-in">
          <div class="relative mb-6">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search resources..."
              class="w-full px-5 py-3 rounded-xl bg-gray-700/50 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-400/30 text-white placeholder-gray-400 backdrop-blur-sm transition-all"
            />
            <svg
              class="absolute right-4 top-3.5 h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>

          <div class="flex flex-wrap gap-3">
            <button
              v-for="category in categories"
              :key="category.id"
              @click="toggleCategory(category.id)"
              :class="[
                'px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2',
                activeCategories.includes(category.id)
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-lg'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50 border border-gray-600'
              ]"
            >
              <span v-html="category.emoji"></span>
              {{ category.name }}
            </button>
          </div>
        </div>

        <!-- Resources Grid -->
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
          <div
            v-for="resource in filteredResources"
            :key="resource.id"
            class="group relative overflow-hidden rounded-2xl bg-gray-800/50 backdrop-blur-sm border border-gray-700 hover:border-blue-400/30 transition-all duration-500 hover:-translate-y-2"
          >
            <div class="p-6">
              <div class="flex items-start gap-4 mb-4">
                <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-600/20 flex items-center justify-center text-2xl">
                  {{ getCategoryEmoji(resource.category) }}
                </div>
                <div>
                  <h3 class="text-lg font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">
                    <a :href="resource.link" target="_blank" rel="noopener noreferrer">{{ resource.title }}</a>
                  </h3>
                  <span class="text-xs font-medium px-2 py-1 rounded-full" 
                        :class="getCategoryColor(resource.category)">
                    {{ getCategoryName(resource.category) }}
                  </span>
                </div>
              </div>
              <p class="text-gray-300 text-sm mb-4">{{ resource.description }}</p>
              
              <div class="flex flex-wrap gap-2 mt-4">
                <span
                  v-for="tag in resource.tags"
                  :key="tag"
                  class="text-xs px-2 py-1 rounded-full bg-gray-700/50 text-gray-300"
                >
                  {{ tag }}
                </span>
              </div>

              <a
                :href="resource.link"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-4 inline-flex items-center text-blue-300 hover:text-cyan-300 group transition-colors text-sm font-medium"
              >
                Visit Resource
                <svg class="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredResources.length === 0" class="text-center py-16 animate-fade-in">
          <div class="text-5xl mb-4">🔍</div>
          <h3 class="text-xl text-gray-300 mb-2">No resources found</h3>
          <p class="text-gray-500 max-w-md mx-auto">Try adjusting your filters or search query</p>
        </div>
      </div>
    </main>

    
    <!-- Footer -->
    <footer class="w-full px-[12.5%] py-12 border-t border-gray-800 bg-gray-900/50">
      <div class="max-w-6xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="mb-6 md:mb-0">
            <h2 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">
              Resources
            </h2>
            <p class="text-gray-400">Leveraging AI to create useful apps.</p>
          </div>
          <div class="flex gap-4">
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">Twitter</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="#" class="text-gray-400 hover:text-blue-300 transition-colors">
              <span class="sr-only">GitHub</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
        <div class="mt-8 pt-8 border-t border-gray-800 text-center text-gray-500 text-sm">
          © 2025 Resources. All rights reserved.
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

// Categories
const categories = ref([
  { id: 'app', name: 'Apps', emoji: '📱' },
  { id: 'tool', name: 'Tools', emoji: '🛠️' },
  { id: 'extension', name: 'Extensions', emoji: '🧩' },
  { id: 'book', name: 'Books', emoji: '📚' },
  { id: 'link', name: 'Links', emoji: '🔗' },
  { id: 'game', name: 'Game', emoji: '🕹️' },
  { id: 'other', name: 'Other', emoji: '✨' }
])

// Mock resources data
const resources = ref([
  {
    id: 1,
    title: 'BlockTheBeat',
    description: 'AI-powered tool to remove background music from videos and audio',    category: 'app',
    link: 'https://blockthebeat.com/',
    tags: ['development', 'open-source', 'ai', 'productivity']
  },
  {
    id: 110,
    title: 'BC Notes',
    description: 'Privacy-focused note-taking app with AI enhancements. Works completely offline and syncs securely across devices.',
    category: 'app',
    link: '#',
    tags: ['privacy', 'ai', 'organization']
  },
  {
    id: 2,
    title: 'Hadeeth.live',
    description: 'Your source for authentic Hadith. Explore, search, and learn from the teachings of Prophet Muhammad (ﷺ).',
    category: 'app',
    tags: ['privacy', 'ai', 'organization'],
    link: 'https://www.hadeeth.live/',
    tags: ['islam', 'hadith', 'sunnah', 'knowledge'],
    img: "hadeethlive"
  },
  {
    id: 3,
    title: 'AlQuraan.live',
    description: 'Listen, read, and explore the Holy Quran. Your comprehensive Quran companion with translations, tafsir, and recitations.',
    category: 'app',
    link: 'https://www.alquraan.live/',
    tags: ['islam', 'quran', 'recitation', 'tafsir', 'translation', 'knowledge'],
    img: "alquraanlive"
  },
  {
    id: 123,
    title: 'BC Assistant',
    description: 'Personal AI mentor that learns your habits and coaches you toward productivity, health, and spiritual goals.',
    category: 'app',
    link: '#',
    tags: ['ai', 'productivity', 'self-improvement']
  },
  {
    id: 4,
    title: 'AI To Hire',
    description: 'Service for businesses to deploy custom AI chatbots as virtual assistants on their websites.',
    category: 'service',
    link: '#',
    tags: ['ai', 'business', 'automation']
  },
  {
    id: 5,
    title: 'ذكرته (Zakartoh)',
    description: 'Minimalist Quran app designed for daily revision (hifz/dhikr) with intuitive navigation and progress tracking.',
    category: 'app',
    link: '#',
    tags: ['islam', 'quran', 'productivity']
  },
  {
    id: 6,
    title: 'اربعين عبد الكبير',
    description: 'Book compiling 40 essential hadiths with concise explanations for practical Islamic living.',
    category: 'book',
    link: '#',
    tags: ['islam', 'hadith', 'education']
  },
  {
    id: 7,
    title: 'تطهير الاعتقاد',
    description: 'Brief yet comprehensive book on authentic Islamic creed (aqeedah), free from innovations.',
    category: 'book',
    link: '#',
    tags: ['islam', 'aqeedah', 'education']
  },
  {
    id: 8,
    title: 'College Maths',
    description: 'Self-study guide covering undergraduate to master’s-level mathematics in a single volume.',
    category: 'book',
    link: '#',
    tags: ['math', 'education', 'reference']
  },
  {
    id: 9,
    title: 'TopDownBattleGround',
    description: 'Fast-paced multiplayer shooter with tactical top-down gameplay and immersive maps.',
    category: 'game',
    link: '#',
    tags: ['action', 'multiplayer', 'competitive']
  },
  {
    id: 10,
    title: 'Unforeseen Onslaught',
    description: 'Psychological horror game with realistic visuals and unpredictable scares.',
    category: 'game',
    link: '#',
    tags: ['horror', 'single-player', 'immersive']
  },
  {
    id: 11,
    title: "Don't Brake the Beak",
    description: 'Casual game where you fly as a bird, dodging obstacles to survive. Simple controls, addictive gameplay.',
    category: 'game',
    link: '#',
    tags: ['casual', 'mobile', 'fun']
  }
])

// State
const searchQuery = ref('')
const activeCategories = ref([])

// Computed properties
const filteredResources = computed(() => {
  let filtered = resources.value

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(resource => 
      resource.title.toLowerCase().includes(query) || 
      resource.description.toLowerCase().includes(query) ||
      resource.tags.some(tag => tag.toLowerCase().includes(query))
)}

  // Filter by categories
  if (activeCategories.value.length > 0) {
    filtered = filtered.filter(resource => 
      activeCategories.value.includes(resource.category))
  }

  return filtered
})

// Methods
function toggleCategory(categoryId) {
  if (activeCategories.value.includes(categoryId)) {
    activeCategories.value = activeCategories.value.filter(id => id !== categoryId)
  } else {
    activeCategories.value.push(categoryId)
  }
}

function getCategoryName(categoryId) {
  const category = categories.value.find(c => c.id === categoryId)
  return category ? category.name : 'Other'
}

function getCategoryEmoji(categoryId) {
  const category = categories.value.find(c => c.id === categoryId)
  return category ? category.emoji : '✨'
}

function getCategoryColor(categoryId) {
  const colors = {
    'app': 'bg-blue-900/40 text-blue-300',
    'tool': 'bg-purple-900/40 text-purple-300',
    'extension': 'bg-indigo-900/40 text-indigo-300',
    'book': 'bg-amber-900/40 text-amber-300',
    'link': 'bg-emerald-900/40 text-emerald-300',
    'other': 'bg-gray-700 text-gray-300'
  }
  return colors[categoryId] || 'bg-gray-700 text-gray-300'
}
</script>

<style>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

.backdrop-blur-sm {
  backdrop-filter: blur(8px);
}

/* Custom link hover effect */
a {
  transition: color 0.2s ease;
}
</style>